/**
 * SKINNED-MESH BVH COLLIDER for the per-vertex cloth path (clothPbd.ts).
 *
 * WHY: cloth collision here was a hand-placed capsule list, and the drifter-shoulder saga came
 * down to a region nobody had declared — the deltoid had NO capsule, so the cape passed through
 * empty space. Colliding against the real skinned surfaces deletes that whole bug class: the
 * cloth avoids whatever the body/armour actually is, not what someone remembered to approximate.
 *
 * HOW: a triangle-region snapshot of a SkinnedMesh, re-skinned on the CPU each refresh into a
 * WORLD-space position buffer, with a three-mesh-bvh MeshBVH refit over it. The cloth solver
 * asks for closest-surface contacts at frame rate and enforces them per substep (clothPbd.ts).
 *
 * COST CONTROL (measured via the stats fields → Avatar.pbdClothStats()):
 *   - REGION FILTER: only triangles inside the cloth's reach (bind-space y band) and NOT
 *     dominated by arm/hand/head bones. Limbs keep their capsules — they genuinely are capsule
 *     shaped; it is the torso/shoulder/gear region where capsules failed. The head never meets
 *     the SIMULATED part of the cloak (everything above the collar split is worn, not cloth).
 *   - MANUAL SKINNING: per refresh each USED bone composes ONE matrix
 *     (matrixWorld · bindMatrixInverse · boneMatrix · bindMatrix — the getVertexPosition
 *     sandwich, distributed over the weighted sum), then each vert is 4 weighted
 *     matrix-applies on flat arrays. ~15 bone composes + ~2k vert transforms per refresh,
 *     far cheaper than getVertexPosition per vert.
 *   - skeleton.boneMatrices are read as the renderer last wrote them (one frame stale) — the
 *     same staleness the rest of the cloth stack already runs at.
 *
 * ⚠ The snapshot copies the source geometry's attributes at build time and never mutates them,
 * so shared garment/body templates stay safe. The source's INDEX is read at build time too —
 * if the covers system later hides body triangles under armour, this collider keeps colliding
 * with them, which is what cloth wants anyway (the surface still exists).
 */
import * as THREE from "three";
import { MeshBVH } from "three-mesh-bvh";

const _mA = new THREE.Matrix4();
const _mB = new THREE.Matrix4();
const _e1 = new THREE.Vector3();
const _e2 = new THREE.Vector3();
const _e3 = new THREE.Vector3();
const _va = new THREE.Vector3();
const _vb = new THREE.Vector3();
const _vc = new THREE.Vector3();
const _hit: { point: THREE.Vector3; distance: number; faceIndex: number } = {
  point: new THREE.Vector3(), distance: 0, faceIndex: 0,
};

export interface BvhColliderOpts {
  /** Bind-space y band of triangles to keep (authored space: feet at 0, metres). */
  yMin?: number;
  yMax?: number;
  /** Exclude triangles whose verts are DOMINANTLY skinned to these bones. */
  excludeBone?: (name: string) => boolean;
  /** Discard the collider when fewer than this many triangles survive the filter. */
  minTris?: number;
}

export class SkinnedBvhCollider {
  /** Triangles in the snapshot; 0 = filter left nothing, callers should drop this collider. */
  readonly triCount: number;
  /** Rolling cost accumulators (ms) — read + reset via Avatar.pbdClothStats(). */
  skinMs = 0;
  refitMs = 0;
  samples = 0;

  private readonly src: THREE.SkinnedMesh;
  private readonly geo: THREE.BufferGeometry;
  private readonly bvh: MeshBVH | null = null;
  /** World-space skinned positions (the BVH's backing store). */
  private readonly pos: Float32Array;
  /** Mesh-local bind positions of the snapshot verts. */
  private readonly bind: Float32Array;
  /** Per-vert 4 bone slots (into boneList) + weights. */
  private readonly idx4: Uint16Array;
  private readonly w4: Float32Array;
  /** Skeleton bone indices actually referenced by the snapshot. */
  private readonly boneList: number[] = [];
  private readonly boneMats: THREE.Matrix4[] = [];
  /** Area-weighted smooth vertex normals, rebuilt each refresh. The face normal alone flips
   *  sign when the closest point lands on an edge or crease (armpit, band boundary) — the
   *  classic signed-distance failure — and a contact built on a flipped normal teleports the
   *  particle THROUGH the surface. Interpolating smooth normals at the closest point
   *  (pseudonormal) keeps the sign stable everywhere. */
  private readonly nrm: Float32Array;

  constructor(src: THREE.SkinnedMesh, opts: BvhColliderOpts = {}) {
    this.src = src;
    const geo = src.geometry;
    const posA = geo.attributes.position as THREE.BufferAttribute | undefined;
    const siA = geo.attributes.skinIndex as THREE.BufferAttribute | undefined;
    const swA = geo.attributes.skinWeight as THREE.BufferAttribute | undefined;
    const index = geo.index;
    if (!posA || !siA || !swA || !index) {
      this.triCount = 0;
      this.geo = new THREE.BufferGeometry();
      this.pos = this.bind = this.nrm = new Float32Array(0);
      this.idx4 = new Uint16Array(0); this.w4 = new Float32Array(0);
      return;
    }
    const yMin = opts.yMin ?? -Infinity, yMax = opts.yMax ?? Infinity;
    const comp = ["getX", "getY", "getZ", "getW"] as const;
    // dominant-bone exclusion per vert
    const excluded = new Uint8Array(posA.count);
    if (opts.excludeBone) {
      const names = src.skeleton.bones.map((b) => b.name);
      const bad = names.map((n) => (opts.excludeBone!(n) ? 1 : 0));
      for (let i = 0; i < posA.count; i++) {
        let bw = -1, bi = 0;
        for (const c of comp) { const w = swA[c](i); if (w > bw) { bw = w; bi = siA[c](i); } }
        if (bad[bi]) excluded[i] = 1;
      }
    }
    const keep = (v: number): boolean => {
      const y = posA.getY(v);
      return y >= yMin && y <= yMax && !excluded[v];
    };
    const tris: number[] = [];
    for (let t = 0; t < index.count; t += 3) {
      const a = index.getX(t), b = index.getX(t + 1), c = index.getX(t + 2);
      if (keep(a) && keep(b) && keep(c)) tris.push(a, b, c);
    }
    if (tris.length / 3 < (opts.minTris ?? 30)) {
      this.triCount = 0;
      this.geo = new THREE.BufferGeometry();
      this.pos = this.bind = this.nrm = new Float32Array(0);
      this.idx4 = new Uint16Array(0); this.w4 = new Float32Array(0);
      return;
    }
    // remap to a compact vert set
    const remap = new Int32Array(posA.count).fill(-1);
    const srcOf: number[] = [];
    for (const v of tris) if (remap[v] < 0) { remap[v] = srcOf.length; srcOf.push(v); }
    const n = srcOf.length;
    this.bind = new Float32Array(n * 3);
    this.pos = new Float32Array(n * 3);
    this.nrm = new Float32Array(n * 3);
    this.idx4 = new Uint16Array(n * 4);
    this.w4 = new Float32Array(n * 4);
    const boneSlot = new Map<number, number>();
    for (let i = 0; i < n; i++) {
      const s = srcOf[i];
      // Snapshot the selected identity before skinning. Mesh's implementation applies
      // morphs without SkinnedMesh's bone transform; Avatar rebuilds on sculpt edits.
      THREE.Mesh.prototype.getVertexPosition.call(src, s, _va);
      this.bind[i * 3] = _va.x; this.bind[i * 3 + 1] = _va.y; this.bind[i * 3 + 2] = _va.z;
      let sum = 0;
      for (const c of comp) sum += swA[c](s);
      const norm = sum > 1e-6 ? 1 / sum : 0;
      comp.forEach((c, k) => {
        const w = swA[c](s) * norm;
        this.w4[i * 4 + k] = w;
        if (w <= 0) { this.idx4[i * 4 + k] = 0; return; }
        const bone = siA[c](s);
        let slot = boneSlot.get(bone);
        if (slot === undefined) {
          slot = this.boneList.length;
          boneSlot.set(bone, slot);
          this.boneList.push(bone);
          this.boneMats.push(new THREE.Matrix4());
        }
        this.idx4[i * 4 + k] = slot;
      });
    }
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3));
    this.geo.setIndex(tris.map((v) => remap[v]));
    this.triCount = tris.length / 3;
    this.refresh(); // positions valid before the BVH builds
    this.bvh = new MeshBVH(this.geo, { maxLeafTris: 8 });
  }

  /** Re-skin the snapshot into world space and refit the BVH. Once per frame (or less). */
  refresh(): void {
    if (!this.pos.length) return;
    const t0 = performance.now();
    this.src.updateWorldMatrix(true, false);
    const bm = this.src.skeleton.boneMatrices;
    // per-bone: matrixWorld · bindMatrixInverse · boneMatrix · bindMatrix — see header
    _mA.copy(this.src.matrixWorld).multiply(this.src.bindMatrixInverse);
    for (let j = 0; j < this.boneList.length; j++) {
      this.boneMats[j].copy(_mA).multiply(_mB.fromArray(bm, this.boneList[j] * 16)).multiply(this.src.bindMatrix);
    }
    const n = this.bind.length / 3;
    for (let i = 0; i < n; i++) {
      const x = this.bind[i * 3], y = this.bind[i * 3 + 1], z = this.bind[i * 3 + 2];
      let ox = 0, oy = 0, oz = 0;
      for (let k = 0; k < 4; k++) {
        const w = this.w4[i * 4 + k];
        if (w <= 0) continue;
        const e = this.boneMats[this.idx4[i * 4 + k]].elements;
        ox += w * (e[0] * x + e[4] * y + e[8] * z + e[12]);
        oy += w * (e[1] * x + e[5] * y + e[9] * z + e[13]);
        oz += w * (e[2] * x + e[6] * y + e[10] * z + e[14]);
      }
      this.pos[i * 3] = ox; this.pos[i * 3 + 1] = oy; this.pos[i * 3 + 2] = oz;
    }
    // area-weighted smooth normals (cross products accumulate area weighting for free)
    this.nrm.fill(0);
    const idx = this.geo.index!;
    for (let t = 0; t < idx.count; t += 3) {
      const a = idx.getX(t), b = idx.getX(t + 1), c = idx.getX(t + 2);
      _va.fromArray(this.pos, a * 3); _vb.fromArray(this.pos, b * 3); _vc.fromArray(this.pos, c * 3);
      _e1.subVectors(_vb, _va); _e2.subVectors(_vc, _va); _e1.cross(_e2);
      for (const v of [a, b, c]) {
        this.nrm[v * 3] += _e1.x; this.nrm[v * 3 + 1] += _e1.y; this.nrm[v * 3 + 2] += _e1.z;
      }
    }
    const t1 = performance.now();
    this.skinMs += t1 - t0;
    this.bvh?.refit();
    this.refitMs += performance.now() - t1;
    this.samples++;
  }

  /** Closest-surface point within `maxDist` of `p` (world). Fills `cp` (surface point) and `n`
   *  (outward face normal); false when nothing is in reach. */
  contact(p: THREE.Vector3, maxDist: number, cp: THREE.Vector3, n: THREE.Vector3): boolean {
    if (!this.bvh) return false;
    if (!this.bvh.closestPointToPoint(p, _hit, 0, maxDist)) return false;
    const idx = this.geo.index!;
    const f = _hit.faceIndex * 3;
    const a = idx.getX(f), b = idx.getX(f + 1), c = idx.getX(f + 2);
    _va.fromArray(this.pos, a * 3); _vb.fromArray(this.pos, b * 3); _vc.fromArray(this.pos, c * 3);
    // barycentrics of the closest point on this face → pseudonormal (see nrm docs)
    _e1.subVectors(_vb, _va); _e2.subVectors(_vc, _va); _e3.subVectors(_hit.point, _va);
    const d00 = _e1.dot(_e1), d01 = _e1.dot(_e2), d11 = _e2.dot(_e2);
    const d20 = _e3.dot(_e1), d21 = _e3.dot(_e2);
    const den = d00 * d11 - d01 * d01;
    if (Math.abs(den) < 1e-14) return false;
    const wb = (d11 * d20 - d01 * d21) / den;
    const wc = (d00 * d21 - d01 * d20) / den;
    const wa = 1 - wb - wc;
    n.set(
      wa * this.nrm[a * 3] + wb * this.nrm[b * 3] + wc * this.nrm[c * 3],
      wa * this.nrm[a * 3 + 1] + wb * this.nrm[b * 3 + 1] + wc * this.nrm[c * 3 + 1],
      wa * this.nrm[a * 3 + 2] + wb * this.nrm[b * 3 + 2] + wc * this.nrm[c * 3 + 2],
    );
    const len = n.length();
    if (len < 1e-10) return false;
    n.multiplyScalar(1 / len);
    cp.copy(_hit.point);
    return true;
  }
}
